import { noddiAsync, URLKeys } from 'noddi-async';
import { CarSpec } from 'noddi-types';
import { AnimatedList, NoddiLinearProgressLoader } from 'noddi-ui-web';
import { compareLicensePlates } from 'noddi-util';
import { RegNumberSearchField, useAuthContext } from 'noddi-web';
import { useEffect, useState } from 'react';
import { BookingCarSearch } from '../../../../hooks/useBookingCarSearch';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCarName } from '../../../../utils/carName';
import { getCommonTranslations } from '../../../../utils/commonTranslations';
import { getErrorCodesWithTrans } from '../../../../utils/getErrorCodesWithTranslations';
import { shouldShowTireHotelBadge } from '../../../../utils/tireHotelDepartment';
import { RemoveCarDialog } from './RemoveCarDialog';
import { SelectCarInBookingCard } from './SelectCarInBookingCard';
import { WheelStorageContractDialog } from './WheelStorageContractDialog';

type SelectableCar = CarSpec & { id?: number };

// Booking dates and tire hotel are nice-to-have. Waiting for them lets a card render once at its
// final height, but a slow endpoint must not hold the list — after this the cards render as-is.
// A failed request never blocks: queries do not retry, so an error ends the loading state.
const ENRICHMENT_WAIT_MS = 2000;

type Props = {
  savedCars?: SelectableCar[];
  loading?: boolean;
  selectedCars?: SelectableCar[];
  /** Owned by the step page, whose Next button submits a typed plate too. */
  carSearch: BookingCarSearch;
  removeCar: (car: CarSpec) => void;
  /**
   * Stationary car step: only show the tire-hotel badge when the hotel's
   * provider department is the one being booked. Mobile omits this.
   */
  serviceDepartmentId?: number;
  userGroupId?: number;
};

export const SelectCars = ({
  savedCars = [],
  loading,
  selectedCars = [],
  carSearch,
  removeCar,
  serviceDepartmentId,
  userGroupId
}: Props) => {
  const [carToRemove, setCarToRemove] = useState<CarSpec | null>(null);
  const { currentBrand, brandEmail } = useCurrentBrand();
  const { currentUserGroupId: selectedUserGroupId, isLoggedIn, userData } = useAuthContext();
  const currentUserGroupId = userGroupId ?? selectedUserGroupId;

  // Every group the viewer belongs to — personal plus any organization — so a
  // company car is enriched even when the private group is selected.
  const viewerUserGroupIds = userData?.user.userGroups.map((userGroup) => userGroup.id) ?? [];

  // Saved cars only: including search-added cars re-keyed this query on every add and
  // re-rendered the whole list. Search-added cars get no booking dates.
  const carIds = savedCars.map((car) => car.id).filter((id): id is number => id != null);
  // Booking-date enrichment only — skip when logged out (session UG can linger without a token).
  const {
    data: fleetCars,
    isLoading: isFleetCarsLoading,
    error: _fleetCarsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      ids: carIds,
      userGroupIds: viewerUserGroupIds,
      brandDomains: [currentBrand]
    },
    queryConfig: {
      enabled: isLoggedIn && carIds.length > 0 && viewerUserGroupIds.length > 0
    }
  });

  const {
    data: tireHotels,
    isLoading: isTireHotelsLoading,
    error: _tireHotelsError
  } = noddiAsync.useGet({
    type: URLKeys.getTireHotelsForCustomer,
    input: {
      userGroupId: currentUserGroupId!,
      activeOnly: true
    },
    queryConfig: {
      // Keyed by user group, so it also covers search-added cars when there are no saved ones.
      enabled: isLoggedIn && (savedCars.length > 0 || selectedCars.length > 0) && !!currentUserGroupId
    }
  });

  // Hold cards until their enrichment is in, so each renders once at its final height — per-card
  // skeletons guessed wrong and a car without history shrank on arrival. `loading` is only true
  // on first load, so pagination reloads the saved list without hiding the search-added cards
  // (until the saved list is known, a selected car may turn out to be one of them).
  const isEnriching = isFleetCarsLoading || isTireHotelsLoading;
  const enrichmentKey = carIds.join(',');
  const [timedOutEnrichmentKey, setTimedOutEnrichmentKey] = useState<string>();
  useEffect(() => {
    if (!isEnriching) {
      return;
    }
    const timer = setTimeout(() => setTimedOutEnrichmentKey(enrichmentKey), ENRICHMENT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isEnriching, enrichmentKey]);
  const waitForEnrichment = isEnriching && timedOutEnrichmentKey !== enrichmentKey;
  const isInitialLoading = loading || (waitForEnrichment && isTireHotelsLoading);
  const isSavedListPending = loading || waitForEnrichment;

  const lastBookingDateByCarId = new Map<number, string>();
  const upcomingBookingDateByCarId = new Map<number, string>();
  for (const car of fleetCars?.results ?? []) {
    // Booking history is owner-only: plate search is open to anyone, so never
    // render dates for a car the viewer does not own directly or via an org.
    const isOwnedByViewer = (car.ownersCurrent ?? []).some((owner) => viewerUserGroupIds.includes(owner.userGroup.id));
    if (!isOwnedByViewer) {
      continue;
    }

    const date = car.latestCompletedBooking?.date;
    if (date) {
      lastBookingDateByCarId.set(car.id, date);
    }
    const upcomingDate = car.upcomingBooking?.date;
    if (upcomingDate) {
      upcomingBookingDateByCarId.set(car.id, upcomingDate);
    }
  }

  const tireHotelByCarId = new Map(
    (tireHotels ?? []).filter((hotel) => hotel.carId != null).map((hotel) => [hotel.carId, hotel])
  );

  const getLastBookingDate = (car: SelectableCar) => (car.id != null ? lastBookingDateByCarId.get(car.id) : undefined);
  const getUpcomingBookingDate = (car: SelectableCar) =>
    car.id != null ? upcomingBookingDateByCarId.get(car.id) : undefined;
  const getTireHotel = (car: SelectableCar) => {
    const hotel =
      (car.id != null ? tireHotelByCarId.get(car.id) : undefined) ??
      (tireHotels ?? []).find((entry) => entry.licensePlateNumber === car.licensePlate.number);
    if (!hotel || !shouldShowTireHotelBadge(hotel.serviceDepartmentId, serviceDepartmentId)) {
      return undefined;
    }
    return hotel;
  };

  const handleRemoveClick = (car: CarSpec) => {
    setCarToRemove(car);
  };

  const carsFromSearch = selectedCars.filter(
    (selectedCar) =>
      !savedCars.some((savedCar) => compareLicensePlates(savedCar.licensePlate, selectedCar.licensePlate))
  );

  const getLicensePlateDescription = (car: SelectableCar) => {
    const { number, personalisedNumber } = car.licensePlate;
    return personalisedNumber ? `${personalisedNumber} (${number})` : number;
  };

  const translations = getCommonTranslations().getRegNumberText();

  const renderCarCard = (car: SelectableCar, isSelected: boolean, action: () => void) => (
    <SelectCarInBookingCard
      key={car.licensePlate.number}
      title={getCarName({ car })}
      image={car.image}
      make={car.make}
      description={getLicensePlateDescription(car)}
      lastBookingDate={getLastBookingDate(car)}
      upcomingBookingDate={getUpcomingBookingDate(car)}
      tireHotel={getTireHotel(car)}
      isSelected={isSelected}
      action={action}
    />
  );

  return (
    <>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-2'>
          <RegNumberSearchField
            search={carSearch.plateSearch}
            onCarFound={carSearch.selectCar}
            errorCodes={getErrorCodesWithTrans(brandEmail)}
            translations={translations}
          />

          {isSavedListPending && <NoddiLinearProgressLoader />}
        </div>
        <div className='flex flex-col gap-3'>
          {!isInitialLoading &&
            carsFromSearch.map((carFromSearch) =>
              renderCarCard(carFromSearch, true, () => handleRemoveClick(carFromSearch))
            )}
          {!isSavedListPending && (
            <AnimatedList delayBetweenItems={100}>
              {savedCars.map((car) => {
                const isSelected = selectedCars.some((selectedCar) =>
                  compareLicensePlates(selectedCar.licensePlate, car.licensePlate)
                );
                return renderCarCard(
                  car,
                  isSelected,
                  isSelected ? () => handleRemoveClick(car) : () => carSearch.selectCar(car)
                );
              })}
            </AnimatedList>
          )}
        </div>
      </div>

      <RemoveCarDialog
        car={carToRemove}
        removeCar={removeCar}
        open={!!carToRemove}
        onClose={() => {
          setCarToRemove(null);
        }}
      />

      <WheelStorageContractDialog
        open={!!carSearch.wheelStorageDialogCar}
        onClose={carSearch.closeWheelStorageDialog}
        car={carSearch.wheelStorageDialogCar}
        addCar={carSearch.addCar}
      />
    </>
  );
};
